import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ozonImage from '~/assets/img/ozon.png';
import { Logo } from '~/components/HeaderV3/components/Logo';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import { CONTENT_ID } from '~/pages/Root/constants';
import { mainStore } from '~/stores';

import HeaderIconBanner from '../HeaderIconBanner/HeaderIconBanner';

import styles from './MobileHeaderMarketplace.module.scss';

type MobileHeaderMarketplaceProps = Omit<
  React.ComponentPropsWithRef<'header'>,
  'children'
>;

const MobileHeaderMarketplace = React.forwardRef<
  HTMLHeadingElement,
  MobileHeaderMarketplaceProps
>((props, ref) => {
  const [transitionRunning, setTransitionRunning] = useState(false);
  const { t } = useTranslation();
  const isOverlayExists = mainStore.isOverlayExists();

  const overlayExistsValueRef = useRef<Nullable<boolean>>(null);

  // Control status bar color based on scroll position
  useStatusBarControl({
    initialColor: 'light-content',
    scrolledColor: 'dark-content',
  });

  useEffect(() => {
    if (isOverlayExists !== overlayExistsValueRef.current) {
      if (overlayExistsValueRef.current !== null) {
        setTransitionRunning(true);
      }

      overlayExistsValueRef.current = isOverlayExists;
    }

    const className = `${styles.noScroll}`;

    const cleanUp = () => {
      if (document.body.classList.contains(className)) {
        document.body.classList.remove(className);
      }
    };

    if (isOverlayExists) {
      const scrollElement = document.getElementById(CONTENT_ID);
      if (scrollElement && scrollElement.scrollTop > 0) {
        scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (!document.body.classList.contains(className)) {
        document.body.classList.add(className);
      }
    } else {
      cleanUp();
    }

    return cleanUp;
  }, [isOverlayExists]);

  return (
    <header {...props} ref={ref}>
      <div
        className={classes([
          styles.root,
          (isOverlayExists || transitionRunning) && styles.pullToFront,
        ])}
      >
        <div className={styles.root__inner}>
          <Logo className={styles.logoMarketplace} />
        </div>
        <div className={styles.bannersWrapper}>
          <HeaderIconBanner
            icon={'certificate'}
            title={t('phrases:originatProduct')}
            subtitle={t('phrases:originalCertified')}
          />
          <HeaderIconBanner
            icon={'plane'}
            title={t('phrases:reliableDelivery')}
            subtitle={t('phrases:speedDelivery')}
          />
          <HeaderIconBanner
            icon={'sale'}
            title={t('phrases:goodPrice')}
            subtitle={t('phrases:discount')}
          />
        </div>
        <div className={styles.bannersWrapper}>
          <HeaderIconBanner
            imageSrc={ozonImage}
            imageAlt="Ozon"
            title={t('phrases:work')}
            subtitle={`1 ${t('translation:year')}`}
            variant="gray"
          />
          <HeaderIconBanner
            icon={'box-de'}
            title={t('translation:ordersV2')}
            subtitle={'152 К'}
            variant="gray"
          />
          <HeaderIconBanner
            icon={'chat-circle'}
            title={t('translation:reviewsV2')}
            subtitle={'60,6 К'}
            variant="gray"
          />
          <HeaderIconBanner
            icon={'star-badge'}
            title={t('phrases:averageProductRating')}
            subtitle={'5 из 5'}
            variant="gray"
          />
        </div>
      </div>
    </header>
  );
});
MobileHeaderMarketplace.displayName = 'MobileHeaderMarketplace';

export default observer(MobileHeaderMarketplace);
